// Web Push no navegador (Configurações e o pedido suave de Mensagens). A permissão só é pedida dentro de um gesto da pessoa:
// ativar() chama o Notification.requestPermission ANTES de qualquer await (o Safari recusa depois). O service worker é o /sw.js,
// que a casca registra (candidato-v2.js).
import { enviarFormulario, obterJson } from './api';
import { url } from './config';

export function suportado() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// iPhone/iPad com o site aberto no navegador: o push só existe com o app na tela de início (iOS 16.4 ou mais novo).
export function iphoneSemApp() {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const instalado = matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
    return ios && !instalado;
}

export function permissao() {
    return 'Notification' in window ? Notification.permission : 'denied';
}

export async function inscricaoAtual() {
    if (!suportado()) {
        return null;
    }
    const registro = await navigator.serviceWorker.getRegistration('/');
    return registro ? registro.pushManager.getSubscription() : null;
}

function _bytes(base64url) {
    const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (base64url.length % 4)) % 4);
    return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
}

function _mesmaChave(inscricao, chave) {
    const atual = inscricao?.options?.applicationServerKey;
    if (!atual) {
        return true;
    }
    const a = new Uint8Array(atual);
    return a.length === chave.length && a.every((v, i) => v === chave[i]);
}

function _formulario(inscricao) {
    const dados = new FormData();
    const json = inscricao.toJSON();
    dados.set('endpoint', json.endpoint);
    dados.set('p256dh', json.keys?.p256dh || '');
    dados.set('auth', json.keys?.auth || '');
    return dados;
}

// O navegador já tem inscrição: ela está ativa na conta desta pessoa? (outra pessoa pode ter usado o aparelho)
export async function ativoNaConta() {
    const inscricao = await inscricaoAtual();
    if (!inscricao || permissao() !== 'granted') {
        return false;
    }
    const dados = new FormData();
    dados.set('endpoint', inscricao.endpoint);
    const r = await enviarFormulario(url('/PainelConfiguracoes/PushEstado'), dados, { silencioso: true }).catch(() => null);
    return !!r?.ativo;
}

// 'ok' | 'negado' (a pessoa ou o navegador recusou) | 'indisponivel' (sem suporte ou push desligado no servidor) | 'erro'.
export async function ativar() {
    if (!suportado()) {
        return 'indisponivel';
    }
    const pedido = permissao() === 'granted' ? Promise.resolve('granted') : Notification.requestPermission();
    let resposta;
    try {
        resposta = await pedido;
    } catch {
        return 'negado';
    }
    if (resposta !== 'granted') {
        return 'negado';
    }
    try {
        const { chave } = await obterJson(url('/PainelConfiguracoes/PushChave'), { semCache: true, silencioso: true });
        if (!chave) {
            return 'indisponivel';
        }
        const bytes = _bytes(chave);
        const registro = (await navigator.serviceWorker.getRegistration('/'))
            || await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
        await navigator.serviceWorker.ready;
        let inscricao = await registro.pushManager.getSubscription();
        // Chave do servidor trocada (rotação do VAPID): a inscrição antiga não serve mais.
        if (inscricao && !_mesmaChave(inscricao, bytes)) {
            await inscricao.unsubscribe();
            inscricao = null;
        }
        if (!inscricao) {
            inscricao = await registro.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes });
        }
        const r = await enviarFormulario(url('/PainelConfiguracoes/PushInscrever'), _formulario(inscricao));
        return r?.sucesso ? 'ok' : 'erro';
    } catch {
        return 'erro';
    }
}

// Saiu da conta (/candidato/sair, o servidor já tirou a inscrição pelo cookie e desconectou): o navegador também deixa o serviço de
// push, e uma inscrição antiga que o servidor não conhecia morre no 404/410 do próximo envio. Sem chamar o servidor.
export async function esquecerAparelho() {
    const inscricao = await inscricaoAtual().catch(() => null);
    if (inscricao) {
        await inscricao.unsubscribe().catch(() => { });
    }
}

// Tira este navegador da conta e do serviço de push.
export async function desativar() {
    const inscricao = await inscricaoAtual();
    if (!inscricao) {
        return true;
    }
    const dados = new FormData();
    dados.set('endpoint', inscricao.endpoint);
    const r = await enviarFormulario(url('/PainelConfiguracoes/PushRemover'), dados).catch(() => null);
    if (!r?.sucesso) {
        return false;
    }
    await inscricao.unsubscribe().catch(() => { });
    return true;
}
